/**
 * Driving behaviour report (ADR 0026): how the forklifts are driven, per operator or per vehicle, for
 * occupational-safety (İSG) coaching. The aggregation runs in SQL (supabase/migrations/
 * 20270412000002_driving.sql, report_driving, SECURITY INVOKER, RLS applies). This module holds the shared
 * limits, the alarm types the report counts, the result shape and small pure helpers. Pure TypeScript.
 *
 * Only transparent numbers, never a score. Harsh acceleration, braking and cornering are not reported:
 * no device sends such events today, and they are not derived from GNSS (too noisy at forklift speeds).
 */
import type { AlarmType } from "./alarms.js";
import type { TenantRole } from "./domain.js";

export const DRIVING_GROUPS = ["operator", "vehicle"] as const;
export type DrivingGroup = (typeof DRIVING_GROUPS)[number];

export const DRIVING_LIMITS = Object.freeze({
  /** Longest date range (local days, inclusive). SQL: report_check(…, 31). */
  maxDays: 31,
  /** Vehicles × days until a load test on managed Postgres. SQL: fts:report_too_large. */
  maxVehicleDays: 400,
  /** Speed band edges (km/h): [1, 5), [5, 10), [10, 15), ≥ 15 — moving starts at 1 km/h. */
  bandsKmh: [5, 10, 15] as readonly number[],
  /** A reported speed above this is a spike: not the highest speed, counted as an outlier. */
  maxPlausibleKmh: 40,
  /** Event rates are given per this many moving hours… */
  rateHours: 10,
  /** …and only from this much moving time on (seconds). */
  rateMinMovingS: 3600,
});

/**
 * The lowest role that may run and export the report: every tenant member who can see reports (owner
 * decision 2026-11-01, ADR 0026 annex). KVKK protection inside the report is unchanged (RLS, hidden
 * operators only counted, a single hidden operator never singled out, exports logged).
 */
export const DRIVING_MIN_ROLE: TenantRole = "viewer";

/** Columns always shown, in this order (even at 0). SQL: c_types. */
export const DRIVING_EVENT_TYPES: readonly AlarmType[] = ["impact", "seat_violation", "belt_violation", "geofence_enter", "geofence_exit", "overload"];
/**
 * Alarm types that say nothing about how the vehicle is driven (vehicle state, card reader, battery,
 * dwell). SQL: c_excluded; checklist alarms are excluded too. Every other alarm type — including future
 * ones such as an overspeed or a harsh-braking event — appears in the report without a change of shape.
 */
export const DRIVING_EXCLUDED_TYPES: readonly AlarmType[] = ["unauthorized_start", "card_denied", "tamper", "power_loss", "geofence_dwell", "deep_discharge", "bat_overtemp", "dvr_storage", "dvr_camera", "device_output_control", "battery_low"];

/** Whether report_driving() counts an alarm type. */
export function drivingCountsType(type: string): boolean {
  return !type.startsWith("checklist") && !(DRIVING_EXCLUDED_TYPES as readonly string[]).includes(type);
}

/** What a row stands for: a visible operator, operators the user may not see, unknown cards, no card, a vehicle. */
export const DRIVING_ROW_KINDS = ["operator", "hidden", "unknown_card", "no_card", "vehicle"] as const;
export type DrivingRowKind = (typeof DRIVING_ROW_KINDS)[number];

export type DrivingMetrics = {
  ignitionS: number;
  movingS: number;
  distanceM: number;
  /** distance ÷ moving time; null without moving time or GNSS distance. */
  avgKmh: number | null;
  /** Highest plausible reported speed; null without speed data. */
  maxKmh: number | null;
  /** Reported speeds above maxPlausibleKmh (left out of maxKmh). */
  speedOutliers: number;
  /** Moving seconds per speed band (4 values, see bandsKmh). */
  bandS: number[];
  /** Impacts by level 1, 2, 3. */
  impacts: [number, number, number];
  /** Events by alarm type (types with at least one event). */
  events: Partial<Record<string, number>>;
  eventsTotal: number;
  /** Per 10 moving hours; null below one moving hour. */
  eventsPer10h: number | null;
  impactsPer10h: number | null;
};

export type DrivingRow = DrivingMetrics & {
  kind: DrivingRowKind;
  /** Operator id (kind operator) or vehicle id (kind vehicle); null for the aggregate rows. */
  id: string | null;
  /** Operator name or fleet number; null for the aggregate rows (the console labels them). */
  name: string | null;
  /** Distinct cards behind a "hidden" or "unknown_card" row. */
  cards: number;
  vehicles: number;
  /**
   * KVKK: a "hidden" row standing for a single card would single out one person the user may not see. The
   * server then returns the count only (every metric null; here 0) and leaves it out of the totals. Never
   * show or export the metrics of a suppressed row.
   */
  suppressed: boolean;
};

/** report_driving(). */
export type DrivingResult = {
  tz: string;
  group: DrivingGroup;
  bandsKmh: number[];
  maxPlausibleKmh: number;
  rateHours: number;
  rateMinMovingS: number;
  /** Event columns: DRIVING_EVENT_TYPES, then any other counted type that occurred. */
  eventTypes: string[];
  rows: DrivingRow[];
  totals: DrivingMetrics & { operators: number; vehicles: number };
};

/** A "hidden operators" row needs at least this many cards to show metrics (KVKK, SQL: < 2 → suppressed). */
export const DRIVING_HIDDEN_MIN_CARDS = 2;

/** Share (%, one decimal) of moving time in each band; all null without moving time. */
export function drivingBandShares(bandS: readonly number[]): (number | null)[] {
  const total = bandS.reduce((a, b) => a + b, 0);
  return bandS.map((s) => (total > 0 ? Math.round((1000 * s) / total) / 10 : null));
}

/** Events per `rateHours` moving hours (2 decimals), like SQL; null below `rateMinMovingS`. */
export function drivingRate(events: number, movingS: number): number | null {
  if (movingS < DRIVING_LIMITS.rateMinMovingS) return null;
  return Math.round((100 * events) / (movingS / 3600 / DRIVING_LIMITS.rateHours)) / 100;
}

/** Labels of the speed bands, e.g. ["< 5", "5–10", "10–15", "≥ 15"] (km/h). */
export function drivingBandLabels(edges: readonly number[] = DRIVING_LIMITS.bandsKmh): string[] {
  return edges.map((e, i) => (i === 0 ? `< ${e}` : `${edges[i - 1]}–${e}`)).concat(`≥ ${edges[edges.length - 1]}`);
}
